import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Global } from '@emotion/react';
import i18next from 'i18next';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';
import App from './App';
import { fetchThemeConfig } from './app-state/slices/ThemeSlice';
import { AppDispatch, RootState } from './app-state/store';
import SpinnerOverlay from './canopy-ui-components/components/SpinnerOverlay/SpinnerOverlay';
import * as ga from './canopy-ui-components/google-analytics';
import {
    setChakraDefaultThemeColor,
    changei18nLanguage,
} from './canopy-ui-components/helpers/localStorage';
import theme from './canopy-ui-components/styles/theme';
import { DEFAULT_LOCALE } from './components/Preferences/LanguageSelector';
import { withBasePath } from './config/app';
import ErrorWL from './pages/errorWL';
import { Fonts } from './styles/Fonts';
import { getColorShades } from './utils';
function AppIndex() {
    const [newTheme, setNewTheme] = useState(extendTheme(theme));
    const [localFavicon, setlocalFavicon] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const [showComp, setShowComp] = useState(false);
    const {
        preference: { isLoading, data, error },
    }: any = useSelector((state: RootState) => state.themeConfig);
    const [errors, setErrors] = useState('');

    useEffect(() => {
        const storedLocale = localStorage.getItem('localeId');

        if (data?.defaultLocale && !storedLocale) {
            // i18next.changeLanguage(data.defaultLocale);
            changei18nLanguage(data.defaultLocale);
        } else if (!data?.defaultLocale && !storedLocale) {
            // i18next.changeLanguage(DEFAULT_LOCALE);
            changei18nLanguage(DEFAULT_LOCALE);
        }
    }, [data?.defaultLocale]);

    useEffect(() => {
        dispatch(fetchThemeConfig());
    }, []);

    useEffect(() => {
        if (data && (data?.colors?.light || data?.colors?.dark)) {
            const defaultTheme = !data?.colors?.light ? 'dark' : 'light';
            setChakraDefaultThemeColor(defaultTheme);
            const hasValidFont =
                data.font?.includes('Roboto') ||
                data.font?.includes('sans-serif');
            const fontFamily = hasValidFont ? data.font : 'Roboto';
            let colorThemes: any = {};
            ['light', 'dark'].forEach((themeKey: string) => {
                if (
                    data?.colors?.[themeKey] &&
                    !isEmpty(data?.colors?.[themeKey])
                ) {
                    colorThemes[themeKey] = {
                        ...(theme as any).colors[themeKey],
                        ...data.colors[themeKey],
                    };
                } else {
                    colorThemes[themeKey] = {
                        ...(theme as any).colors[themeKey],
                    };
                }
            });
            let images: any = {};
            for (let key in data?.images) {
                images[key] = withBasePath(data?.images?.[key]);
            }
            const newTheme = extendTheme({
                ...theme,
                ...data,
                colors: {
                    ...theme.colors,
                    ...data.colors,
                    ...colorThemes,
                    ...(data?.colors?.dark
                        ? {
                              darkPrimary: getColorShades(
                                  data?.colors?.dark?.primaryColor ||
                                      (theme as any).colors.dark?.primaryColor
                              ),
                          }
                        : {}),
                    ...(data?.colors?.light
                        ? {
                              lightPrimary: getColorShades(
                                  data?.colors?.light?.primaryColor ||
                                      (theme as any).colors.light?.primaryColor
                              ),
                          }
                        : {}),
                },
                fonts: {
                    body: fontFamily,
                    heading: fontFamily,
                    mono: fontFamily,
                },
                images: { ...images },
                initialColorMode: defaultTheme,
                config: {
                    initialColorMode: defaultTheme,
                },
                isMainline: data?.partnerCode === 'mainline',
            });
            if (data?.images?.favicon) {
                setlocalFavicon(true);
            } else {
                setlocalFavicon(false);
            }
            setNewTheme(newTheme);
            setShowComp(true);
        } else if (data) {
            setErrors('error');
            setChakraDefaultThemeColor('light');
            setlocalFavicon(false);
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            setErrors('error');
            setChakraDefaultThemeColor('light');
            setlocalFavicon(false);
        }
    }, [error]);

    if (isLoading) {
        return (
            <>
                <div className="load-align">
                    <div className="loader"></div>
                </div>
            </>
        );
    } else {
        return (
            <React.Suspense fallback="">
                <ChakraProvider
                    theme={newTheme}
                    resetCSS={false}
                    portalZIndex={4}>
                    {showComp ? (
                        <>
                            <Fonts />
                            <Global
                                styles={{
                                    '*': {
                                        fontFamily: newTheme.fonts.body,
                                    },
                                }}
                            />
                            <IntercomProvider
                                appId={
                                    newTheme?.isMainline
                                        ? String(
                                              process.env
                                                  .REACT_APP_INTERCOM_APP_ID
                                          )
                                        : ''
                                }
                                onHide={() => {
                                    ga.event({
                                        action: `Intercom > Close`,
                                    });
                                }}
                                onShow={() => {
                                    ga.event({
                                        action: `Intercom > Open`,
                                    });
                                }}>
                                <BrowserRouter basename={withBasePath('/')}>
                                    <Helmet>
                                        <title>{data?.appName || ' '}</title>
                                        <link
                                            rel="icon"
                                            type="image/png"
                                            href={
                                                !localFavicon
                                                    ? withBasePath(
                                                          '/images/error_favicon.ico'
                                                      )
                                                    : withBasePath(
                                                          data.images.favicon
                                                      )
                                            }
                                        />
                                        {data?.partnerCode === 'mainline' && (
                                            <>
                                                <link
                                                    rel="manifest"
                                                    href={withBasePath(
                                                        '/manifest.json'
                                                    )}
                                                />
                                                <link
                                                    rel="apple-touch-icon"
                                                    href={withBasePath(
                                                        '/icons/icon-180x180.png'
                                                    )}
                                                />
                                            </>
                                        )}
                                        <meta
                                            name="viewport"
                                            content="width=device-width, minimum-scale=1.0, maximum-scale = 5.0"></meta>
                                        <meta
                                            name="theme-color"
                                            content="#ffffff"
                                        />
                                    </Helmet>
                                    <App />
                                </BrowserRouter>
                            </IntercomProvider>
                        </>
                    ) : errors ? (
                        <React.Suspense fallback="">
                            <ErrorWL />
                        </React.Suspense>
                    ) : (
                        <SpinnerOverlay text={'loading'}></SpinnerOverlay>
                    )}
                </ChakraProvider>
            </React.Suspense>
        );
    }
}

export default AppIndex;
